import { useState } from 'react';
import type { FormEvent } from 'react';
import type { DotFolioPolicy } from '../api/contracts';
import { useMonitorApi } from '../app/api-context';
import { useAuth } from '../auth/auth-context';
import { useAsyncResource } from '../hooks/use-async-resource';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingBlock } from '../components/Feedback';

const labels: Record<number, string> = {
  33: 'Factura',
  34: 'Factura exenta',
  39: 'Boleta',
  41: 'Boleta exenta',
  52: 'Guía de despacho',
  56: 'Nota de débito',
  61: 'Nota de crédito',
};
function PolicyForm({
  policy,
  editable,
  pending,
  reload,
}: {
  policy: DotFolioPolicy;
  editable: boolean;
  pending: boolean;
  reload: () => void;
}) {
  const api = useMonitorApi();
  const [automatic, setAutomatic] = useState(policy.automaticReplenishment);
  const [size, setSize] = useState(policy.batchSize);
  const [minimum, setMinimum] = useState(policy.minimumRemaining);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice('');
    try {
      await api.saveDotFolioPolicy({
        documentType: policy.documentType,
        automaticReplenishment: automatic,
        batchSize: size,
        minimumRemaining: minimum,
        expectedRevision: policy.revision,
      });
      reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  async function request() {
    setBusy(true);
    setError(null);
    setNotice('');
    try {
      await api.requestDotFolioBlock({
        documentType: policy.documentType,
        quantity: policy.batchSize,
      });
      setNotice('Solicitud guardada como pendiente. No se envió a Dot.');
      reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel" aria-label={`${labels[policy.documentType]} ${policy.documentType}`}>
      <div className="panel__body">
        <h2>
          {labels[policy.documentType]} · {policy.documentType}
        </h2>
        <form onSubmit={(e) => void save(e)}>
          <fieldset disabled={!editable || busy} style={{ border: 0, padding: 0 }}>
            <div className="filters">
              <label className="field">
                <span className="field__label">Folios por bloque</span>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={10000}
                  required
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                />
              </label>
              <label className="field">
                <span className="field__label">Umbral mínimo</span>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={Math.max(0, size - 1)}
                  required
                  value={minimum}
                  onChange={(e) => setMinimum(Number(e.target.value))}
                />
              </label>
              <label className="field">
                <span>
                  <input
                    type="checkbox"
                    checked={automatic}
                    onChange={(e) => setAutomatic(e.target.checked)}
                  />{' '}
                  Reposición automática al habilitar la integración
                </span>
              </label>
            </div>
            <button className="button button--primary" type="submit">
              Guardar configuración
            </button>{' '}
            <button
              className="button"
              type="button"
              disabled={pending || size !== policy.batchSize}
              onClick={() => void request()}
            >
              {pending ? 'Solicitud pendiente' : `Preparar solicitud de ${policy.batchSize} folios`}
            </button>
          </fieldset>
        </form>
        {error !== null && <ErrorBanner error={error} />}
        {notice && <p role="status">{notice}</p>}
      </div>
    </section>
  );
}
export function DotFoliosPage() {
  const api = useMonitorApi();
  const { user } = useAuth();
  const resource = useAsyncResource(() => api.getDotFolios(), [api]);
  const data = resource.data;
  return (
    <div className="page">
      <header className="page-header">
        <h1>Facturador · Dot Solutions</h1>
        <p>Configuración de folios por tipo de documento.</p>
      </header>
      <section className="panel">
        <div className="panel__body">
          <strong>Preparación local: emisión y reserva de folios deshabilitadas.</strong>
          <p>
            Guardar una preferencia o preparar una solicitud no contacta a Dot. Las solicitudes
            quedarán pendientes hasta completar y habilitar la integración.
          </p>
          {data?.scope ? (
            <p>
              {data.scope.environment === 'certification' ? 'Certificación' : 'Producción'} · RUT
              emisor {data.scope.emitterRut}
            </p>
          ) : (
            data && <p>Falta configurar el RUT emisor en la API para guardar cambios.</p>
          )}
        </div>
      </section>
      {resource.error !== null && <ErrorBanner error={resource.error} />}
      {resource.loading && <LoadingBlock />}
      {data?.policies.map((policy) => (
        <PolicyForm
          key={`${policy.documentType}-${policy.revision}`}
          policy={policy}
          editable={user?.role === 'admin' && data.scope !== null}
          pending={data.requests.some((r) => r.documentType === policy.documentType)}
          reload={resource.reload}
        />
      ))}
      {data && data.requests.length > 0 && (
        <section className="panel">
          <div className="panel__body">
            <h2>Solicitudes pendientes de envío</h2>
            <ul>
              {data.requests.map((r) => (
                <li key={r.id}>
                  {labels[r.documentType]} · {r.quantity} folios · {r.requestedBy} · No enviada
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}
